/**
 * seq-gate-store · per-subscription chain-sequence high-water mark.
 *
 * The realtime apply path (`useRealtimeQuery` → `_runDispatchBranch`)
 * consults this before applying a NATS event to the TanStack cache. It
 * closes three holes in blind apply:
 *
 *   1. Snapshot race — the response carries one conservative pre-read
 *      `lastSeq` fence; NATS deltas at or below it are dropped by the agreed
 *      best-effort handoff, not proof that every bucket includes that event.
 *   2. Reconnect replay — JetStream replays from the persisted stream seq,
 *      which (throttled persistence — see `last-seq-store.ts`) can lag the
 *      truly-processed point, so already-applied events get re-delivered.
 *   3. At-least-once redelivery — JetStream ack timeouts.
 *
 * MODEL — one high-water cursor per `(queryKey, subjectPattern)`:
 *   admit an event iff its `sequenceId` is STRICTLY newer than the
 *   high-water, then advance the high-water to it.
 *
 * Why a plain high-water is sufficient for `set`, `add` AND `prepend`
 * recipes alike — JetStream delivers events IN ORDER within a single
 * consumer, and each `(queryKey, subjectPattern)` binds exactly one
 * consumer. So within a gate key the only non-monotonic arrivals are
 * duplicates (cases 1-3) — never a genuine reorder. A strict-greater
 * high-water drops exactly the duplicates and nothing else, so `add`
 * counters never double-count and `prepend` rows never duplicate without
 * needing a separate seen-set. Cross-subject interleaving (a market row
 * patched by `.trade` / `.boost` / `.backing` / `.lifecycle`) is handled
 * by keying per subject pattern — each subject advances its own cursor.
 *
 * Platform-agnostic (SDK core, web + RN): module-level singleton, no
 * platform APIs — mirrors `last-seq-store.ts`.
 */

import { compareCursor } from './sequence-id.js';

/**
 * Cap on tracked gate keys — (mounted realtime queries × their subject
 * patterns). Generous: a 50-card list × ~4 subjects = 200 keys. Beyond
 * the cap the oldest keys are FIFO-evicted; an evicted key simply
 * re-seeds from the next snapshot fetch when its query next mounts.
 */
const MAX_GATE_KEYS = 4096;

/** gateKey → newest applied chain cursor (15-byte `sequenceId`). */
const highWater = new Map<string, Uint8Array>();

/**
 * Compose the gate key. `queryHash` is TanStack's `hashKey(queryKey)`;
 * `subjectPattern` is the SDK-computed wildcard pattern the event matched
 * (`MorfiEvent.subjectPattern`).
 */
export function gateKey(queryHash: string, subjectPattern: string): string {
  return `${queryHash} ${subjectPattern}`;
}

function evictIfOver(): void {
  if (highWater.size <= MAX_GATE_KEYS) return;
  // Map iterates in insertion order — drop oldest first until under cap.
  for (const k of highWater.keys()) {
    if (highWater.size <= MAX_GATE_KEYS) break;
    highWater.delete(k);
  }
}

/**
 * Seed the high-water for a gate key from a REST snapshot's `lastSeq`
 * watermark. Called when a realtime query's snapshot fetch resolves —
 * every subsequent NATS delta `<=` this cursor is dropped by the agreed
 * pre-read fence. Monotonic: a seed equal-or-older than the current high-water
 * is ignored, so a late stale snapshot cannot rewind the gate.
 */
export function seedGate(key: string, cursor: Uint8Array): void {
  const prev = highWater.get(key);
  if (prev !== undefined && compareCursor(cursor, prev) <= 0) return;
  highWater.delete(key);
  highWater.set(key, cursor);
  evictIfOver();
}

/**
 * Decide whether a NATS event may be applied, and record it in one call.
 *
 * Returns `true` iff `cursor` is strictly newer than the gate's
 * high-water (or the gate is unseeded) — in which case the high-water is
 * advanced. Returns `false` for a duplicate / stale event, leaving the
 * high-water untouched.
 *
 * Event dispatch is synchronous, so the combined check-and-record has no
 * race.
 */
export function admitEvent(key: string, cursor: Uint8Array): boolean {
  if (!wouldAdmitEvent(key, cursor)) return false;
  highWater.delete(key);
  highWater.set(key, cursor);
  evictIfOver();
  return true;
}

/** Read-only admission check used before payload validation and cache apply. */
export function wouldAdmitEvent(key: string, cursor: Uint8Array): boolean {
  const prev = highWater.get(key);
  return prev === undefined || compareCursor(cursor, prev) > 0;
}

/** Devtool / test seam — current high-water for a gate key. */
export function peekGate(key: string): Uint8Array | undefined {
  return highWater.get(key);
}

/** Drop a single gate key (optional — e.g. on query unmount). */
export function clearGate(key: string): void {
  highWater.delete(key);
}

/** A replacement chart baseline starts a fresh per-query replay fence. */
export function clearGatesForQuery(queryHash: string): void {
  const prefix = `${queryHash}\0`;
  for (const key of highWater.keys()) {
    if (key.startsWith(prefix)) highWater.delete(key);
  }
}

/**
 * Drop every gate key. Hooked by the SDK's logout-cache-clear path (the
 * same place that calls `clearAllLastSeqs`) so a new account does not
 * inherit the previous account's watermarks.
 */
export function clearAllGates(): void {
  highWater.clear();
}

/** Test seam — number of tracked gate keys. */
export function _gateCount(): number {
  return highWater.size;
}
